/**
 * Anode Pricing Calculator
 * Calculates customer prices based on supplier cost and pricing strategy
 */

import { supabase } from '../supabase/client.js';

/**
 * Fetches the current pricing strategy from the database
 * @returns {Promise<Object>} Pricing strategy configuration
 */
export async function getPricingStrategy() {
  try {
    const { data, error} = await supabase
      .from('pricing_settings')
      .select('setting_value')
      .eq('setting_key', 'anode_pricing_strategy')
      .single();

    if (error) {
      console.error('Error fetching pricing strategy:', error);
      // Return default strategy if database query fails
      return getDefaultPricingStrategy();
    }

    return data.setting_value;
  } catch (err) {
    console.error('Exception fetching pricing strategy:', err);
    return getDefaultPricingStrategy();
  }
}

/**
 * Default pricing strategy (fallback)
 */
function getDefaultPricingStrategy() {
  return {
    enabled: true,
    default_strategy: 'percentage_markup',
    percentage_markup: {
      rate: 50,
      description: 'Add 50% to supplier cost'
    },
    minimum_markup_dollars: 2.00,
    round_to_nearest_cent: true
  };
}

/**
 * Calculate customer price from supplier cost
 * @param {number} supplierCost - The cost you pay to supplier (sale_price)
 * @param {Object} options - Additional options (category, anode details)
 * @returns {Promise<Object>} { customerPrice, markup, marginPercent, strategy }
 */
export async function calculateAnodePrice(supplierCost, options = {}) {
  const strategy = await getPricingStrategy();

  if (!strategy.enabled) {
    // Pricing disabled, return cost as-is
    return {
      customerPrice: supplierCost,
      markup: 0,
      marginPercent: 0,
      marginDollars: 0,
      strategy: 'disabled'
    };
  }

  let customerPrice = supplierCost;
  let strategyUsed = strategy.default_strategy;

  // Apply strategy in priority order
  if (strategy.category_pricing?.enabled && options.category) {
    // Category-based pricing (shaft, prop, hull, etc.)
    const categoryConfig = strategy.category_pricing.categories[options.category];
    if (categoryConfig) {
      const markupPercent = categoryConfig.markup_percent;
      customerPrice = supplierCost * (1 + markupPercent / 100);
      strategyUsed = 'category_pricing';
    }
  } else if (strategy.tiered_pricing?.enabled) {
    // Tiered pricing based on cost ranges
    const tier = strategy.tiered_pricing.tiers.find(t => {
      const aboveMin = supplierCost >= t.min_cost;
      const belowMax = t.max_cost === null || supplierCost < t.max_cost;
      return aboveMin && belowMax;
    });

    if (tier) {
      customerPrice = supplierCost * (1 + tier.markup_percent / 100);
      strategyUsed = 'tiered_pricing';
    }
  } else if (strategy.default_strategy === 'percentage_markup') {
    // Simple percentage markup
    const markupPercent = strategy.percentage_markup.rate;
    customerPrice = supplierCost * (1 + markupPercent / 100);
    strategyUsed = 'percentage_markup';
  } else if (strategy.default_strategy === 'fixed_markup') {
    // Fixed dollar amount markup
    customerPrice = supplierCost + strategy.fixed_markup.amount;
    strategyUsed = 'fixed_markup';
  }

  // Apply minimum markup
  if (strategy.minimum_markup_dollars) {
    const minPrice = supplierCost + strategy.minimum_markup_dollars;
    if (customerPrice < minPrice) {
      customerPrice = minPrice;
      strategyUsed += '_with_minimum';
    }
  }

  // Round to nearest cent if configured
  if (strategy.round_to_nearest_cent) {
    customerPrice = Math.round(customerPrice * 100) / 100;
  }

  const marginDollars = customerPrice - supplierCost;
  const marginPercent = supplierCost > 0 ? (marginDollars / supplierCost * 100) : 0;

  return {
    customerPrice: Number(customerPrice.toFixed(2)),
    supplierCost: Number(supplierCost.toFixed(2)),
    markup: Number(marginDollars.toFixed(2)),
    marginDollars: Number(marginDollars.toFixed(2)),
    marginPercent: Number(marginPercent.toFixed(1)),
    strategy: strategyUsed
  };
}

/**
 * Calculate prices for multiple anodes (FAST - fetches strategy once)
 * @param {Array} anodes - Array of anodes with supplier_cost and category
 * @returns {Promise<Array>} Array of anodes with pricing details
 */
export async function calculateAnodePrices(anodes) {
  // Fetch pricing strategy ONCE for all anodes
  const strategy = await getPricingStrategy();

  // Calculate prices for all anodes using the same strategy (no more DB calls)
  return anodes.map(anode => {
    const supplierCost = anode.supplier_cost || anode.sale_price || 0;
    const pricing = calculateAnodePriceWithStrategy(supplierCost, strategy, {
      category: anode.category
    });

    return {
      ...anode,
      ...pricing
    };
  });
}

/**
 * Calculate customer price from supplier cost using a provided strategy
 * (Does NOT fetch from database - use for batch operations)
 * @param {number} supplierCost - The cost you pay to supplier
 * @param {Object} strategy - Pricing strategy configuration
 * @param {Object} options - Additional options (category, anode details)
 * @returns {Object} { customerPrice, markup, marginPercent, strategy }
 */
export function calculateAnodePriceWithStrategy(supplierCost, strategy, options = {}) {
  if (!strategy.enabled) {
    return {
      customerPrice: supplierCost,
      markup: 0,
      marginPercent: 0,
      marginDollars: 0,
      strategy: 'disabled'
    };
  }

  let customerPrice = supplierCost;
  let strategyUsed = strategy.default_strategy;

  // Apply strategy in priority order
  if (strategy.category_pricing?.enabled && options.category) {
    const categoryConfig = strategy.category_pricing.categories[options.category];
    if (categoryConfig) {
      const markupPercent = categoryConfig.markup_percent;
      customerPrice = supplierCost * (1 + markupPercent / 100);
      strategyUsed = 'category_pricing';
    }
  } else if (strategy.tiered_pricing?.enabled) {
    const tier = strategy.tiered_pricing.tiers.find(t => {
      const aboveMin = supplierCost >= t.min_cost;
      const belowMax = t.max_cost === null || supplierCost < t.max_cost;
      return aboveMin && belowMax;
    });

    if (tier) {
      customerPrice = supplierCost * (1 + tier.markup_percent / 100);
      strategyUsed = 'tiered_pricing';
    }
  } else if (strategy.default_strategy === 'percentage_markup') {
    const markupPercent = strategy.percentage_markup.rate;
    customerPrice = supplierCost * (1 + markupPercent / 100);
    strategyUsed = 'percentage_markup';
  } else if (strategy.default_strategy === 'fixed_markup') {
    customerPrice = supplierCost + strategy.fixed_markup.amount;
    strategyUsed = 'fixed_markup';
  }

  // Apply minimum markup
  if (strategy.minimum_markup_dollars) {
    const minPrice = supplierCost + strategy.minimum_markup_dollars;
    if (customerPrice < minPrice) {
      customerPrice = minPrice;
      strategyUsed += '_with_minimum';
    }
  }

  // Round to nearest cent if configured
  if (strategy.round_to_nearest_cent) {
    customerPrice = Math.round(customerPrice * 100) / 100;
  }

  const marginDollars = customerPrice - supplierCost;
  const marginPercent = supplierCost > 0 ? (marginDollars / supplierCost * 100) : 0;

  return {
    customerPrice: Number(customerPrice.toFixed(2)),
    supplierCost: Number(supplierCost.toFixed(2)),
    markup: Number(marginDollars.toFixed(2)),
    marginDollars: Number(marginDollars.toFixed(2)),
    marginPercent: Number(marginPercent.toFixed(1)),
    strategy: strategyUsed
  };
}

/**
 * Format price for display
 * @param {number} price - Price to format
 * @returns {string} Formatted price (e.g., "$45.50")
 */
export function formatPrice(price) {
  if (price === null || price === undefined) return '-';
  return `$${Number(price).toFixed(2)}`;
}

/**
 * Format margin for display
 * @param {number} marginPercent - Margin percentage
 * @returns {string} Formatted margin (e.g., "50.0%")
 */
export function formatMargin(marginPercent) {
  if (marginPercent === null || marginPercent === undefined) return '-';
  return `${Number(marginPercent).toFixed(1)}%`;
}

/**
 * Update pricing strategy
 * @param {Object} newStrategy - New pricing strategy configuration
 * @param {string} updatedBy - Email of user making the update
 * @returns {Promise<Object>} Updated strategy
 */
export async function updatePricingStrategy(newStrategy, updatedBy) {
  const { data, error } = await supabase
    .from('pricing_settings')
    .update({
      setting_value: newStrategy,
      updated_by: updatedBy
    })
    .eq('setting_key', 'anode_pricing_strategy')
    .select()
    .single();

  if (error) throw error;
  return data;
}
